// RBAC Phase 4: what the OIDC login page does when a passkey ceremony fails. Server errors come from
// auth-server `/auth/passkey/*` (AuthServerError: status + `{ error }` body); browser errors are the
// WebAuthn DOMExceptions.
//
// Passkey hardening: a staff account with no passkey registers its first one only with a one-time
// enrolment code an IAM admin issues (403 ENROLMENT_TOKEN_REQUIRED / ENROLMENT_TOKEN_INVALID);
// adding or removing another needs a passkey confirmation in this session first (401
// PASSKEY_REAUTH_REQUIRED); and a passkey younger than the cooldown cannot satisfy a critical-action
// check yet (403 PASSKEY_TOO_NEW).

export type PasskeyErrorAction =
  | 'enrol'
  | 'reauth'
  | 'enrolment-code'
  | 'passkey-reauth'
  | 'too-new'
  | 'cancelled'
  | 'error';

function serverError(err: unknown): { status: number; code: string; body: Record<string, unknown> } | null {
  const e = err as { status?: unknown; body?: unknown } | null;
  if (!e || typeof e.status !== 'number') return null;
  const body = (e.body && typeof e.body === 'object' ? e.body : {}) as Record<string, unknown>;
  const code = typeof body.error === 'string' ? body.error : typeof body.code === 'string' ? body.code : '';
  return { status: e.status, code, body };
}

const domName = (err: unknown) => (err && typeof err === 'object' && typeof (err as { name?: unknown }).name === 'string' ? (err as { name: string }).name : '');

export function passkeyErrorAction(err: unknown): PasskeyErrorAction {
  const s = serverError(err);
  if (s?.status === 409 && s.code === 'NO_PASSKEYS') return 'enrol';
  if (s?.status === 401 && s.code === 'REAUTH_REQUIRED') return 'reauth';
  if (s?.status === 401 && s.code === 'PASSKEY_REAUTH_REQUIRED') return 'passkey-reauth';
  if (s?.status === 403 && (s.code === 'ENROLMENT_TOKEN_REQUIRED' || s.code === 'ENROLMENT_TOKEN_INVALID')) return 'enrolment-code';
  if (s?.status === 403 && s.code === 'PASSKEY_TOO_NEW') return 'too-new';
  const name = domName(err);
  if (name === 'NotAllowedError' || name === 'AbortError') return 'cancelled';
  return 'error';
}

/**
 * Minutes until a new passkey can satisfy a critical-action check, from a PASSKEY_TOO_NEW body:
 * a numeric `cooldownMinutes` / `minutes` field when auth-server sends one, else the number in its
 * message ("… 15 minutes after it was added."). null when neither is there.
 */
export function passkeyCooldownMinutes(err: unknown): number | null {
  const s = serverError(err);
  if (!s) return null;
  for (const key of ['cooldownMinutes', 'minutes', 'retryAfterMinutes']) {
    const v = s.body[key];
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) return Math.ceil(v);
  }
  const message = typeof s.body.message === 'string' ? s.body.message : '';
  const m = /(\d+)\s*min/i.exec(message);
  return m ? Number(m[1]) : null;
}

export function passkeyTooNewText(minutes: number | null): string {
  return minutes
    ? `Your new passkey is saved. It can be used for critical actions after ${minutes} minute${minutes === 1 ? '' : 's'}; try again then.`
    : 'Your new passkey is saved. It can be used for critical actions after a short waiting period; try again later.';
}

export function passkeyErrorText(err: unknown): string {
  const s = serverError(err);
  if (s?.code === 'REAUTH_REQUIRED') return 'For your security, sign in again before adding a passkey.';
  if (s?.code === 'NO_PASSKEYS') return 'You have no passkey yet. Register one to continue.';
  if (s?.code === 'ENROLMENT_TOKEN_REQUIRED') {
    return 'Adding your first passkey needs an enrolment code. Ask an IAM admin for a passkey enrolment code, then enter it here.';
  }
  if (s?.code === 'ENROLMENT_TOKEN_INVALID') {
    return 'That enrolment code is not valid: it may have expired, been used or replaced by a newer one. Ask an IAM admin for a new code.';
  }
  if (s?.code === 'PASSKEY_REAUTH_REQUIRED') return 'Confirm with one of your existing passkeys first, then try again.';
  if (s?.code === 'PASSKEY_TOO_NEW') return passkeyTooNewText(passkeyCooldownMinutes(err));
  switch (domName(err)) {
    case 'NotAllowedError':
    case 'AbortError':
      return 'The passkey prompt was cancelled or timed out. Try again.';
    case 'InvalidStateError':
      return 'This passkey is already registered for your account.';
    case 'NotSupportedError':
    case 'SecurityError':
      return 'This browser or address cannot use passkeys here. Use a current browser on the console address.';
    default:
      return err instanceof Error && err.message ? err.message : 'Passkey sign-in failed. Try again.';
  }
}

/** A pasted enrolment code: surrounding whitespace and inner spaces/line breaks removed. */
export function normalizeEnrolmentCode(raw: string | null | undefined): string {
  return (raw ?? '').replace(/\s+/g, '').slice(0, 512);
}

/** The `enrolmentToken` body field for `/auth/passkey/register/*`: omitted when there is no code. */
export function enrolmentTokenBody(code: string | null | undefined): { enrolmentToken?: string } {
  const token = normalizeEnrolmentCode(code);
  return token ? { enrolmentToken: token } : {};
}

type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export const ENROLMENT_CODE_STORAGE_KEY = 'c4y.admin.passkeyEnrolmentCode';

function sessionStore(): KeyValueStorage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * A `?enrolment=<code>` link survives the sign-in round trip (the login page is left for
 * `/oauth/authorize` and comes back with `redirect=`): the code from the URL is kept in this tab's
 * sessionStorage and read back when the passkey step shows. The URL value wins over a stored one.
 */
export function rememberEnrolmentCode(search: string, storage: KeyValueStorage | null = sessionStore()): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const fromUrl = normalizeEnrolmentCode(params.get('enrolment'));
  try {
    if (fromUrl) {
      storage?.setItem(ENROLMENT_CODE_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return normalizeEnrolmentCode(storage?.getItem(ENROLMENT_CODE_STORAGE_KEY));
  } catch {
    return fromUrl;
  }
}

/** A code is single use: forget it once a passkey was registered with it (or it was refused). */
export function forgetEnrolmentCode(storage: KeyValueStorage | null = sessionStore()): void {
  try {
    storage?.removeItem(ENROLMENT_CODE_STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
}
